import React, { useEffect, useState } from "react";
import { MapPin, Plus, Trash2, Loader2, Check, X } from "lucide-react";
import { getProfile, addAddress, deleteAddress } from "../lib/api";
import { SERVICE_AREAS, SERVICEABLE_PINCODES } from "../mock";

const AddressBook = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ label: "Home", address: "", city: SERVICE_AREAS[0], pincode: "" });

  useEffect(() => {
    getProfile()
      .then((p) => setAddresses(p.addresses || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const inZone = form.pincode.length === 6 && SERVICEABLE_PINCODES.has(form.pincode);

  const reset = () => { setForm({ label: "Home", address: "", city: SERVICE_AREAS[0], pincode: "" }); setError(""); setShowForm(false); };

  const save = async () => {
    setError("");
    if (!form.address.trim() || form.pincode.length !== 6) { setError("Please fill the address and a 6-digit pincode."); return; }
    if (!inZone) { setError("Sorry, we don't deliver to this pincode (beyond 20 km of Sector 78, Noida)."); return; }
    setSaving(true);
    try {
      const res = await addAddress({ label: form.label.trim() || "Home", address: form.address.trim(), city: form.city, pincode: form.pincode });
      setAddresses(res.addresses || []);
      reset();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save the address. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this saved address?")) return;
    try {
      const res = await deleteAddress(id);
      setAddresses(res.addresses || []);
    } catch {}
  };

  return (
    <div className="bg-white rounded-2xl bakery-shadow p-5 mb-6" data-testid="address-book">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin size={18} className="text-[#D46A8A]" />
          <h2 className="font-display text-lg font-bold text-[#3A2417]">My Delivery Addresses</h2>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#D46A8A] text-white text-sm hover:bg-[#c25a7b]" data-testid="address-book-add-btn">
            <Plus size={15} /> Add address
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-8 grid place-items-center text-[#B07A5C]"><Loader2 size={22} className="animate-spin" /></div>
      ) : (
        <>
          {addresses.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              {addresses.map((a) => (
                <div key={a.id} className="relative rounded-xl border border-[#EADBCB] bg-[#FBF3EA] p-3" data-testid={`address-item-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#6B4226] bg-white border border-[#EADBCB] rounded-full px-2 py-0.5">{a.label || a.city}</span>
                    <button onClick={() => remove(a.id)} aria-label="Remove address" className="grid place-items-center w-6 h-6 rounded-full hover:bg-red-50" data-testid={`address-delete-${a.id}`}>
                      <Trash2 size={14} className="text-[#B07A5C] hover:text-red-500" />
                    </button>
                  </div>
                  <div className="text-sm text-[#3A2417] mt-1.5">{a.address}</div>
                  <div className="text-xs text-[#B07A5C] mt-0.5">{a.city} - {a.pincode}</div>
                </div>
              ))}
            </div>
          ) : (
            !showForm && <p className="text-sm text-[#B07A5C] mt-4">No saved addresses yet. Add one to check out faster next time.</p>
          )}

          {showForm && (
            <div className="mt-4 rounded-xl border border-dashed border-[#EADBCB] p-4 space-y-3" data-testid="address-book-form">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#3A2417]">New delivery location</h3>
                <button onClick={reset} className="w-7 h-7 grid place-items-center rounded-full hover:bg-[#F6E7DA]"><X size={16} className="text-[#6B4226]" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#6B4226]">Label</label>
                  <input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Home / Office" className="w-full mt-1 h-10 px-3 rounded-lg bg-[#FBF3EA] border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]" data-testid="address-label-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6B4226]">City / Area</label>
                  <select value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className="w-full mt-1 h-10 px-3 rounded-lg bg-[#FBF3EA] border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]">
                    {SERVICE_AREAS.map((a) => <option key={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B4226]">Full Address</label>
                <input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="House / flat, street, landmark" className="w-full mt-1 h-10 px-3 rounded-lg bg-[#FBF3EA] border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]" data-testid="address-street-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6B4226]">Pincode</label>
                <input value={form.pincode} onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="6-digit pincode" inputMode="numeric" className="w-full mt-1 h-10 px-3 rounded-lg bg-[#FBF3EA] border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]" data-testid="address-pincode-input" />
                {form.pincode.length === 6 && (
                  inZone
                    ? <p className="text-xs text-[#3f6b2e] mt-1 flex items-center gap-1"><Check size={13} /> We deliver to this pincode.</p>
                    : <p className="text-xs text-red-600 mt-1">Outside our 20 km delivery zone.</p>
                )}
              </div>
              {error && <p className="text-sm text-red-600" data-testid="address-book-error">{error}</p>}
              <button onClick={save} disabled={saving} className="w-full h-11 rounded-full bg-[#D46A8A] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#c25a7b] disabled:opacity-60" data-testid="address-save-btn">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Save address
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddressBook;
